import React, { useRef, useState, useEffect } from 'react';
import SignaturePad from 'signature_pad';
import { useTheme } from '../../contexts/ThemeContext';

interface DigitalSignatureProps {
  onSignatureCapture: (signatureDataUrl: string) => void;
  onCancel?: () => void;
  width?: number;
  height?: number;
  className?: string;
  showControls?: boolean;
  readonly?: boolean;
  initialSignature?: string;
}

const DigitalSignature: React.FC<DigitalSignatureProps> = ({
  onSignatureCapture,
  onCancel,
  width = 500,
  height = 200,
  className = '',
  showControls = true,
  readonly = false,
  initialSignature
}) => {
  const { isDarkMode } = useTheme();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const signaturePadRef = useRef<SignaturePad | null>(null);
  const [isEmpty, setIsEmpty] = useState(true);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    // Initialize SignaturePad
    const signaturePad = new SignaturePad(canvas, {
      backgroundColor: isDarkMode ? '#374151' : '#ffffff',
      penColor: isDarkMode ? '#ffffff' : '#000000',
      minWidth: 1,
      maxWidth: 3,
      throttle: 16,
      minDistance: 2,
      velocityFilterWeight: 0.7,
      dotSize: 0
    });

    signaturePadRef.current = signaturePad;

    // Handle signature events
    const handleBeginStroke = () => {
      setIsEmpty(false);
    };

    const handleEndStroke = () => {
      setIsEmpty(signaturePad.isEmpty());
    };

    signaturePad.addEventListener('beginStroke', handleBeginStroke);
    signaturePad.addEventListener('endStroke', handleEndStroke);

    // Load initial signature if provided
    if (initialSignature && initialSignature.startsWith('data:image/')) {
      signaturePad.fromDataURL(initialSignature)
        .then(() => {
          setIsEmpty(false);
        })
        .catch((error) => {
          console.error('Error loading initial signature:', error);
        });
    }

    // Handle canvas resize for high DPI screens
    const resizeCanvas = () => {
      const ratio = Math.max(window.devicePixelRatio || 1, 1);
      canvas.width = canvas.offsetWidth * ratio;
      canvas.height = canvas.offsetHeight * ratio;
      canvas.getContext('2d')?.scale(ratio, ratio);
      signaturePad.clear();
      
      // Reload initial signature after resize
      if (initialSignature && initialSignature.startsWith('data:image/')) {
        signaturePad.fromDataURL(initialSignature)
          .then(() => {
            setIsEmpty(false);
          })
          .catch((error) => {
            console.error('Error reloading signature after resize:', error);
          });
      }
    };

    // Set initial canvas size
    resizeCanvas();

    // Handle window resize
    window.addEventListener('resize', resizeCanvas);

    // Disable signature pad if readonly
    if (readonly) {
      signaturePad.off();
    }

    return () => {
      window.removeEventListener('resize', resizeCanvas);
      signaturePad.removeEventListener('beginStroke', handleBeginStroke);
      signaturePad.removeEventListener('endStroke', handleEndStroke);
      signaturePad.off();
    };
  }, [isDarkMode, readonly, initialSignature]);

  const handleClear = () => {
    if (signaturePadRef.current) {
      signaturePadRef.current.clear();
      setIsEmpty(true);
    }
  };

  const handleSave = () => {
    if (signaturePadRef.current && !signaturePadRef.current.isEmpty()) {
      try {
        // Generate high-quality PNG with transparent background
        const dataURL = signaturePadRef.current.toDataURL('image/png');
        onSignatureCapture(dataURL);
      } catch (error) {
        console.error('Error saving signature:', error);
        alert('Error saving signature. Please try again.');
      }
    } else {
      alert('Please provide a signature before saving.');
    }
  };

  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    }
  };

  return (
    <div className={`signature-container ${className}`}>
      <div className={`relative border-2 rounded-lg ${
        isDarkMode ? 'border-gray-600 bg-gray-700' : 'border-gray-300 bg-white'
      }`}>
        <canvas
          ref={canvasRef}
          width={width}
          height={height}
          style={{ 
            width: `${width}px`, 
            height: `${height}px`,
            display: 'block',
            borderRadius: '6px'
          }}
          className="touch-action-none"
        />
        
        {isEmpty && !readonly && (
          <div className={`absolute inset-0 flex items-center justify-center pointer-events-none ${
            isDarkMode ? 'text-gray-400' : 'text-gray-500'
          }`}>
            <span className="text-sm">Sign here</span>
          </div>
        )}
      </div>

      {showControls && !readonly && (
        <div className="flex justify-between items-center mt-4 gap-3">
          <button
            onClick={handleClear}
            disabled={isEmpty}
            className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
              isEmpty
                ? isDarkMode 
                  ? 'bg-gray-700 text-gray-500 cursor-not-allowed'
                  : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                : isDarkMode
                  ? 'bg-red-600 text-white hover:bg-red-700'
                  : 'bg-red-600 text-white hover:bg-red-700'
            }`}
          >
            Clear
          </button>

          <div className="flex gap-2">
            {onCancel && (
              <button
                onClick={handleCancel}
                className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                  isDarkMode
                    ? 'bg-gray-600 text-white hover:bg-gray-700'
                    : 'bg-gray-500 text-white hover:bg-gray-600'
                }`}
              >
                Cancel
              </button>
            )}
            
            <button
              onClick={handleSave}
              disabled={isEmpty}
              className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                isEmpty
                  ? isDarkMode 
                    ? 'bg-gray-700 text-gray-500 cursor-not-allowed'
                    : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  : isDarkMode
                    ? 'bg-blue-600 text-white hover:bg-blue-700'
                    : 'bg-blue-600 text-white hover:bg-blue-700'
              }`}
            >
              Save Signature
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default DigitalSignature; 